/**
 * Icon Utilities — Lucide React icon system for the portal
 * Provides icon mapping, rendering, and picker library
 */
import React from 'react';
import {
  ClipboardList, FileText, Wrench, Settings, Search, Users, User, Building, Building2,
  BarChart, BarChart3, PieChart, Calendar, Clock, Camera, MapPin, PenTool, Pencil, Hash,
  ChevronDownSquare, ToggleLeft, CheckSquare, CircleDot, Tag, QrCode,
  Minus, Heading, Repeat, Save, Trash2, Plus, X, Check, Star, Heart,
  Flag, Bookmark, Bell, Home, Folder, FolderOpen, Upload, Download, Link,
  Mail, Lock, Unlock, Info, Zap, Briefcase, CreditCard, DollarSign, TrendingUp,
  TrendingDown, Landmark, Handshake, Receipt, FileSignature,
  Laptop, Monitor, Smartphone, Keyboard, Printer, Plug, Battery,
  Wifi, Shield, ShieldCheck, Lightbulb, Flashlight, Telescope,
  Car, Bus, Truck, Plane, Train, Ship, Bike,
  TreePine, Trees, Leaf, Flower2, Sun, Moon, Cloud, Waves, Flame,
  Dog, Cat, Bird, Fish, Bug,
  FolderClosed, FileStack, FileBarChart,
  Droplets, Mountain, Tent, Globe,
  Microscope, TestTube, FlaskConical, Ruler, Scale, Thermometer, Pill, Dna, Atom,
  Magnet, Hammer, Cog, Pickaxe, Axe, Nut,
  Compass, Factory, Construction, Target, Satellite,
  Phone, AtSign, Inbox, Send, Megaphone, MessageSquare,
  CloudRain, CloudSnow, Snowflake, Umbrella, Wind, Tornado, Rainbow,
  AlarmClock, Timer, Hourglass, Watch,
  AlertTriangle, CircleAlert, HelpCircle, Trophy,
  UserCheck, UserPlus, UserX,
  Play, Pause, Square, SkipForward, SkipBack, RefreshCw, Shuffle,
  ArrowUp, ArrowDown, ArrowLeft, ArrowRight, ChevronDown,
  HardHat, Glasses, ShieldAlert, Ban, Siren, Bandage,
  TestTubes, Package, Layers, ClipboardCheck,
  BrickWall, Hospital, Stethoscope, Syringe, Brain, Eye,
  Award, CircleCheck, CircleX, Book, BookOpen,
  Shapes, Sparkles, Database, Server, Key, KeyRound,
  List, Table2, Grid3x3, ChevronRight, Paperclip,
  Barcode, Image, PenLine,
  GripVertical, Copy, MoreVertical, Filter, SlidersHorizontal,
  ChevronLeft, ArrowUpDown, RotateCcw, ExternalLink,
  EyeOff, Power, LogOut, Menu,
} from 'lucide-react';

// ─── Lucide Icon Map ─────────────────────────────────────────────────
// Maps string names → React components for dynamic rendering

export const LUCIDE_ICON_MAP = {
  'clipboard-list': ClipboardList,
  'file-text': FileText,
  'wrench': Wrench,
  'settings': Settings,
  'search': Search,
  'users': Users,
  'user': User,
  'building': Building,
  'building-2': Building2,
  'bar-chart': BarChart,
  'bar-chart-3': BarChart3,
  'pie-chart': PieChart,
  'calendar': Calendar,
  'clock': Clock,
  'camera': Camera,
  'map-pin': MapPin,
  'pen-tool': PenTool,
  'pencil': Pencil,
  'hash': Hash,
  'chevron-down-square': ChevronDownSquare,
  'toggle-left': ToggleLeft,
  'check-square': CheckSquare,
  'circle-dot': CircleDot,
  'tag': Tag,
  'qr-code': QrCode,
  'minus': Minus,
  'heading': Heading,
  'repeat': Repeat,
  'save': Save,
  'trash-2': Trash2,
  'plus': Plus,
  'x': X,
  'check': Check,
  'star': Star,
  'heart': Heart,
  'flag': Flag,
  'bookmark': Bookmark,
  'bell': Bell,
  'home': Home,
  'folder': Folder,
  'folder-open': FolderOpen,
  'folder-closed': FolderClosed,
  'upload': Upload,
  'download': Download,
  'link': Link,
  'mail': Mail,
  'lock': Lock,
  'unlock': Unlock,
  'info': Info,
  'zap': Zap,
  'briefcase': Briefcase,
  'credit-card': CreditCard,
  'dollar-sign': DollarSign,
  'trending-up': TrendingUp,
  'trending-down': TrendingDown,
  'landmark': Landmark,
  'handshake': Handshake,
  'receipt': Receipt,
  'file-signature': FileSignature,
  'laptop': Laptop,
  'monitor': Monitor,
  'smartphone': Smartphone,
  'keyboard': Keyboard,
  'printer': Printer,
  'plug': Plug,
  'battery': Battery,
  'wifi': Wifi,
  'shield': Shield,
  'shield-check': ShieldCheck,
  'lightbulb': Lightbulb,
  'flashlight': Flashlight,
  'telescope': Telescope,
  'car': Car,
  'bus': Bus,
  'truck': Truck,
  'plane': Plane,
  'train': Train,
  'ship': Ship,
  'bike': Bike,
  'tree-pine': TreePine,
  'trees': Trees,
  'leaf': Leaf,
  'flower': Flower2,
  'sun': Sun,
  'moon': Moon,
  'cloud': Cloud,
  'waves': Waves,
  'flame': Flame,
  'dog': Dog,
  'cat': Cat,
  'bird': Bird,
  'fish': Fish,
  'bug': Bug,
  'file-stack': FileStack,
  'file-bar-chart': FileBarChart,
  'droplets': Droplets,
  'mountain': Mountain,
  'tent': Tent,
  'globe': Globe,
  'microscope': Microscope,
  'test-tube': TestTube,
  'test-tubes': TestTubes,
  'flask-conical': FlaskConical,
  'ruler': Ruler,
  'scale': Scale,
  'thermometer': Thermometer,
  'pill': Pill,
  'dna': Dna,
  'atom': Atom,
  'magnet': Magnet,
  'hammer': Hammer,
  'cog': Cog,
  'pickaxe': Pickaxe,
  'axe': Axe,
  'nut': Nut,
  'compass': Compass,
  'factory': Factory,
  'construction': Construction,
  'target': Target,
  'satellite': Satellite,
  'phone': Phone,
  'at-sign': AtSign,
  'inbox': Inbox,
  'send': Send,
  'megaphone': Megaphone,
  'message-square': MessageSquare,
  'cloud-rain': CloudRain,
  'cloud-snow': CloudSnow,
  'snowflake': Snowflake,
  'umbrella': Umbrella,
  'wind': Wind,
  'tornado': Tornado,
  'rainbow': Rainbow,
  'alarm-clock': AlarmClock,
  'timer': Timer,
  'hourglass': Hourglass,
  'watch': Watch,
  'alert-triangle': AlertTriangle,
  'circle-alert': CircleAlert,
  'help-circle': HelpCircle,
  'trophy': Trophy,
  'user-check': UserCheck,
  'user-plus': UserPlus,
  'user-x': UserX,
  'play': Play,
  'pause': Pause,
  'square': Square,
  'skip-forward': SkipForward,
  'skip-back': SkipBack,
  'refresh-cw': RefreshCw,
  'shuffle': Shuffle,
  'arrow-up': ArrowUp,
  'arrow-down': ArrowDown,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'chevron-down': ChevronDown,
  'hard-hat': HardHat,
  'glasses': Glasses,
  'shield-alert': ShieldAlert,
  'ban': Ban,
  'siren': Siren,
  'bandage': Bandage,
  'package': Package,
  'layers': Layers,
  'clipboard-check': ClipboardCheck,
  'brick-wall': BrickWall,
  'hospital': Hospital,
  'stethoscope': Stethoscope,
  'syringe': Syringe,
  'brain': Brain,
  'eye': Eye,
  'award': Award,
  'circle-check': CircleCheck,
  'circle-x': CircleX,
  'book': Book,
  'book-open': BookOpen,
  'shapes': Shapes,
  'sparkles': Sparkles,
  'database': Database,
  'server': Server,
  'key': Key,
  'key-round': KeyRound,
  'list': List,
  'table-2': Table2,
  'grid-3x3': Grid3x3,
  'chevron-right': ChevronRight,
  'paperclip': Paperclip,
  'barcode': Barcode,
  'image': Image,
  'pen-line': PenLine,
  'grip-vertical': GripVertical,
  'copy': Copy,
  'more-vertical': MoreVertical,
  'filter': Filter,
  'sliders-horizontal': SlidersHorizontal,
  'chevron-left': ChevronLeft,
  'arrow-up-down': ArrowUpDown,
  'rotate-ccw': RotateCcw,
  'external-link': ExternalLink,
  'eye-off': EyeOff,
  'power': Power,
  'log-out': LogOut,
  'menu': Menu,
};

// ─── Render Helper ───────────────────────────────────────────────────

/**
 * Render a form icon from either an emoji string or Lucide name string.
 * Used across pages that display form icons (FormsPage, Layout sidebar, etc.)
 *
 * @param {string} icon - Emoji character or Lucide icon name (e.g. 'clipboard-list')
 * @param {number} size - Icon size in px (default 18)
 * @param {Object} props - Additional props passed to the Lucide component
 * @returns {React.ReactNode}
 */
export function renderFormIcon(icon, size = 18, props = {}) {
  if (!icon) return <ClipboardList size={size} {...props} />;
  const LucideComponent = LUCIDE_ICON_MAP[icon];
  if (LucideComponent) return <LucideComponent size={size} {...props} />;
  // Fallback: render as text (legacy emoji or unknown string)
  return <span style={{ fontSize: size, lineHeight: 1 }}>{icon}</span>;
}

// ─── Field Type Icon Map ─────────────────────────────────────────────
// Maps form field types to Lucide components (used by ComponentLibrary, pdfFieldMapper, etc.)

export const FIELD_TYPE_ICONS = {
  // Internal format (after schema conversion)
  'input': Pencil,
  'textarea': FileText,
  'select': ChevronDownSquare,
  'checkbox': CheckSquare,
  'checkbox_group': CheckSquare,
  'radio': CircleDot,
  'date': Calendar,
  'time': Clock,
  'datetime': Calendar,
  'number': Hash,
  'currency': Hash,
  'percentage': Hash,
  'toggle': ToggleLeft,
  'tags': Tag,
  'camera': Camera,
  'image_camera': Camera,
  'geolocation': MapPin,
  'location': MapPin,
  'drawing': PenTool,
  'qr_scanner': QrCode,
  'barcode_scanner': Barcode,
  'repeated_form': Repeat,
  'divider': Minus,
  'header': Heading,
  // API format aliases (used by ExcelExportConfigPage mapFieldType and system fields)
  'text': Pencil,
  'email': Mail,
  'phone': Phone,
  'url': Link,
  'image': Camera,
  'voice': FileText,
  'file': FileText,
  'checkboxgroup': CheckSquare,
  'repeatedform': Repeat,
  // Semantic types for system/work-order fields
  'user': User,
  // PDF schema types
  'qrcode': QrCode,
  'barcode': Barcode,
  'code39': Barcode,
  'code128': Barcode,
  'ean13': Barcode,
  'ean8': Barcode,
  'upca': Barcode,
  'upce': Barcode,
  'itf14': Barcode,
  'nw7': Barcode,
  'japanpost': Barcode,
  'gs1datamatrix': Barcode,
  'pdf417': Barcode,
  'table': Table2,
  'line': Minus,
  'rectangle': Square,
  'ellipse': CircleAlert,
};

/**
 * Get a Lucide icon component for a field type
 * @param {string} fieldType
 * @param {number} size
 * @returns {React.ReactNode}
 */
export function renderFieldTypeIcon(fieldType, size = 16, props = {}) {
  const IconComponent = FIELD_TYPE_ICONS[fieldType] || Pencil;
  return <IconComponent size={size} {...props} />;
}

// ─── Icon Picker Library ─────────────────────────────────────────────

export const ICON_PICKER_LIBRARY = {
  'General': [
    { name: 'check', component: Check, keywords: ['check', 'checkmark', 'done', 'complete', 'approved', 'success'] },
    { name: 'star', component: Star, keywords: ['star', 'favorite', 'important', 'featured', 'highlight', 'best'] },
    { name: 'heart', component: Heart, keywords: ['heart', 'love', 'favorite', 'like', 'important', 'care'] },
    { name: 'flag', component: Flag, keywords: ['flag', 'marker', 'important', 'attention', 'milestone', 'indicator'] },
    { name: 'bookmark', component: Bookmark, keywords: ['bookmark', 'save', 'mark', 'tag', 'reference', 'favorite'] },
    { name: 'bell', component: Bell, keywords: ['bell', 'notification', 'alert', 'reminder', 'alarm', 'notice'] },
    { name: 'home', component: Home, keywords: ['home', 'house', 'main', 'start', 'dashboard', 'beginning'] },
    { name: 'settings', component: Settings, keywords: ['settings', 'gear', 'config', 'preferences', 'options', 'tools'] },
    { name: 'search', component: Search, keywords: ['search', 'find', 'magnify', 'look', 'explore', 'discover'] },
    { name: 'user', component: User, keywords: ['user', 'person', 'profile', 'account', 'individual', 'member'] },
    { name: 'folder-open', component: FolderOpen, keywords: ['folder', 'directory', 'files', 'organize', 'storage', 'documents'] },
    { name: 'trash-2', component: Trash2, keywords: ['trash', 'delete', 'remove', 'bin', 'discard', 'garbage'] },
    { name: 'pencil', component: Pencil, keywords: ['edit', 'pencil', 'write', 'modify', 'change', 'update'] },
    { name: 'save', component: Save, keywords: ['save', 'disk', 'floppy', 'store', 'backup', 'preserve'] },
    { name: 'upload', component: Upload, keywords: ['upload', 'send', 'outbox', 'export', 'share', 'submit'] },
    { name: 'download', component: Download, keywords: ['download', 'receive', 'inbox', 'import', 'get', 'retrieve'] },
    { name: 'link', component: Link, keywords: ['link', 'chain', 'connect', 'url', 'hyperlink', 'attachment'] },
    { name: 'clipboard-list', component: ClipboardList, keywords: ['clipboard', 'copy', 'paste', 'list', 'form', 'document'] },
    { name: 'bar-chart-3', component: BarChart3, keywords: ['chart', 'graph', 'analytics', 'data', 'stats', 'report'] },
    { name: 'calendar', component: Calendar, keywords: ['calendar', 'date', 'schedule', 'time', 'event', 'day'] },
    { name: 'mail', component: Mail, keywords: ['email', 'mail', 'message', 'letter', 'contact', 'envelope'] },
    { name: 'lock', component: Lock, keywords: ['lock', 'secure', 'private', 'protected', 'security', 'password'] },
    { name: 'unlock', component: Unlock, keywords: ['unlock', 'open', 'unlocked', 'access', 'available', 'public'] },
    { name: 'plus', component: Plus, keywords: ['plus', 'add', 'new', 'create', 'more', 'increase'] },
    { name: 'minus', component: Minus, keywords: ['minus', 'subtract', 'remove', 'less', 'decrease', 'reduce'] },
    { name: 'x', component: X, keywords: ['close', 'cancel', 'delete', 'remove', 'exit', 'no'] },
    { name: 'info', component: Info, keywords: ['info', 'information', 'help', 'details', 'about', 'question'] },
    { name: 'zap', component: Zap, keywords: ['lightning', 'fast', 'quick', 'power', 'speed', 'energy'] },
  ],
  'Business & Finance': [
    { name: 'briefcase', component: Briefcase, keywords: ['briefcase', 'business', 'work', 'job', 'professional', 'office'] },
    { name: 'credit-card', component: CreditCard, keywords: ['credit', 'card', 'payment', 'debit', 'transaction', 'finance'] },
    { name: 'trending-up', component: TrendingUp, keywords: ['chart', 'increasing', 'growth', 'profit', 'stocks', 'trending'] },
    { name: 'trending-down', component: TrendingDown, keywords: ['chart', 'decreasing', 'decline', 'loss', 'stocks', 'trending'] },
    { name: 'bar-chart-3', component: BarChart3, keywords: ['bar', 'chart', 'graph', 'statistics', 'analytics', 'data'] },
    { name: 'landmark', component: Landmark, keywords: ['bank', 'banking', 'finance', 'institution', 'money', 'building'] },
    { name: 'building-2', component: Building2, keywords: ['office', 'building', 'corporate', 'company', 'business', 'work'] },
    { name: 'handshake', component: Handshake, keywords: ['handshake', 'deal', 'agreement', 'partnership', 'collaboration', 'contract'] },
    { name: 'receipt', component: Receipt, keywords: ['receipt', 'invoice', 'bill', 'payment', 'transaction', 'proof'] },
    { name: 'hash', component: Hash, keywords: ['numbers', 'calculator', 'math', 'accounting', 'digits', 'finance'] },
    { name: 'file-signature', component: FileSignature, keywords: ['writing', 'hand', 'signature', 'sign', 'contract', 'agreement'] },
    { name: 'key-round', component: KeyRound, keywords: ['key', 'access', 'license', 'permission', 'unlock', 'security'] },
  ],
  'Technology': [
    { name: 'laptop', component: Laptop, keywords: ['laptop', 'computer', 'notebook', 'macbook', 'device', 'technology'] },
    { name: 'monitor', component: Monitor, keywords: ['desktop', 'computer', 'monitor', 'pc', 'screen', 'workstation'] },
    { name: 'smartphone', component: Smartphone, keywords: ['mobile', 'phone', 'smartphone', 'iphone', 'device', 'cellular'] },
    { name: 'keyboard', component: Keyboard, keywords: ['keyboard', 'typing', 'input', 'keys', 'computer', 'peripheral'] },
    { name: 'printer', component: Printer, keywords: ['printer', 'print', 'output', 'document', 'copy', 'peripheral'] },
    { name: 'save', component: Save, keywords: ['floppy', 'disk', 'save', 'storage', 'data', 'file'] },
    { name: 'plug', component: Plug, keywords: ['plug', 'electric', 'power', 'outlet', 'connection', 'cable'] },
    { name: 'battery', component: Battery, keywords: ['battery', 'power', 'charge', 'energy', 'electric', 'cell'] },
    { name: 'wifi', component: Wifi, keywords: ['wifi', 'wireless', 'internet', 'network', 'connection', 'signal'] },
    { name: 'shield', component: Shield, keywords: ['security', 'locked', 'key', 'password', 'encrypted', 'secure'] },
    { name: 'lightbulb', component: Lightbulb, keywords: ['bulb', 'light', 'idea', 'innovation', 'smart', 'solution'] },
    { name: 'flashlight', component: Flashlight, keywords: ['flashlight', 'torch', 'light', 'beam', 'portable', 'illumination'] },
    { name: 'telescope', component: Telescope, keywords: ['telescope', 'astronomy', 'space', 'observe', 'science', 'explore'] },
    { name: 'camera', component: Camera, keywords: ['camera', 'photo', 'picture', 'image', 'photography', 'capture'] },
    { name: 'database', component: Database, keywords: ['database', 'storage', 'data', 'server', 'sql', 'table'] },
    { name: 'qr-code', component: QrCode, keywords: ['qr', 'code', 'scan', 'barcode', 'mobile', 'link'] },
  ],
  'Transportation': [
    { name: 'car', component: Car, keywords: ['car', 'automobile', 'vehicle', 'drive', 'transport', 'sedan'] },
    { name: 'bus', component: Bus, keywords: ['bus', 'vehicle', 'public', 'transport', 'coach', 'commute'] },
    { name: 'truck', component: Truck, keywords: ['truck', 'delivery', 'vehicle', 'transport', 'cargo', 'freight'] },
    { name: 'plane', component: Plane, keywords: ['airplane', 'plane', 'flight', 'aviation', 'travel', 'aircraft'] },
    { name: 'train', component: Train, keywords: ['train', 'locomotive', 'railway', 'transport', 'rail', 'commute'] },
    { name: 'ship', component: Ship, keywords: ['ship', 'boat', 'vessel', 'maritime', 'ocean', 'transport'] },
    { name: 'bike', component: Bike, keywords: ['bicycle', 'bike', 'cycle', 'pedal', 'transport', 'eco'] },
  ],
  'Nature & Environment': [
    { name: 'trees', component: Trees, keywords: ['tree', 'deciduous', 'nature', 'forest', 'plant', 'environment'] },
    { name: 'tree-pine', component: TreePine, keywords: ['tree', 'evergreen', 'pine', 'forest', 'nature', 'christmas'] },
    { name: 'leaf', component: Leaf, keywords: ['leaf', 'plant', 'green', 'nature', 'organic', 'herb'] },
    { name: 'flower', component: Flower2, keywords: ['flower', 'blossom', 'nature', 'plant', 'garden', 'rose'] },
    { name: 'sun', component: Sun, keywords: ['sun', 'sunny', 'bright', 'day', 'weather', 'light'] },
    { name: 'moon', component: Moon, keywords: ['moon', 'crescent', 'night', 'lunar', 'sky', 'celestial'] },
    { name: 'cloud', component: Cloud, keywords: ['cloud', 'weather', 'sky', 'overcast', 'cloudy', 'nature'] },
    { name: 'waves', component: Waves, keywords: ['wave', 'water', 'ocean', 'sea', 'surf', 'nature'] },
    { name: 'flame', component: Flame, keywords: ['fire', 'flame', 'burn', 'hot', 'heat', 'energy'] },
    { name: 'droplets', component: Droplets, keywords: ['water', 'drop', 'liquid', 'blue', 'clean', 'pure'] },
    { name: 'mountain', component: Mountain, keywords: ['mountain', 'peak', 'hill', 'climb', 'nature', 'rock'] },
    { name: 'globe', component: Globe, keywords: ['earth', 'world', 'globe', 'planet', 'international', 'map'] },
    { name: 'dog', component: Dog, keywords: ['dog', 'pet', 'puppy', 'animal', 'canine', 'loyal'] },
    { name: 'cat', component: Cat, keywords: ['cat', 'pet', 'kitten', 'animal', 'feline', 'kitty'] },
    { name: 'bird', component: Bird, keywords: ['bird', 'avian', 'animal', 'fly', 'feather', 'wildlife'] },
    { name: 'fish', component: Fish, keywords: ['fish', 'aquatic', 'animal', 'sea', 'ocean', 'marine'] },
    { name: 'bug', component: Bug, keywords: ['bug', 'insect', 'beetle', 'nature', 'pest', 'debug'] },
  ],
  'Forms & Documents': [
    { name: 'clipboard-list', component: ClipboardList, keywords: ['clipboard', 'form', 'document', 'list', 'checklist', 'survey'] },
    { name: 'file-text', component: FileText, keywords: ['document', 'page', 'file', 'paper', 'text', 'report'] },
    { name: 'file-stack', component: FileStack, keywords: ['document', 'bookmark', 'file', 'page', 'tab', 'stack'] },
    { name: 'pencil', component: Pencil, keywords: ['write', 'note', 'pencil', 'edit', 'memo', 'text'] },
    { name: 'bar-chart-3', component: BarChart3, keywords: ['chart', 'graph', 'data', 'analytics', 'statistics', 'bar'] },
    { name: 'trending-up', component: TrendingUp, keywords: ['chart', 'graph', 'trending', 'up', 'growth', 'increase'] },
    { name: 'trending-down', component: TrendingDown, keywords: ['chart', 'graph', 'trending', 'down', 'decline', 'decrease'] },
    { name: 'folder-open', component: FolderOpen, keywords: ['folder', 'directory', 'file', 'organize', 'open', 'storage'] },
    { name: 'folder-closed', component: FolderClosed, keywords: ['folder', 'directory', 'file', 'organize', 'closed', 'archive'] },
    { name: 'layers', component: Layers, keywords: ['layers', 'stack', 'organize', 'group', 'collection', 'arrange'] },
    { name: 'clipboard-check', component: ClipboardCheck, keywords: ['clipboard', 'checklist', 'inspection', 'audit', 'approved', 'done'] },
    { name: 'book-open', component: BookOpen, keywords: ['book', 'reading', 'manual', 'documentation', 'guide', 'reference'] },
  ],
  'Water & Environment': [
    { name: 'droplets', component: Droplets, keywords: ['water', 'drop', 'liquid', 'blue', 'clean', 'pure'] },
    { name: 'waves', component: Waves, keywords: ['wave', 'water', 'ocean', 'sea', 'surf', 'tide'] },
    { name: 'leaf', component: Leaf, keywords: ['herb', 'plant', 'leaf', 'green', 'nature', 'organic'] },
    { name: 'trees', component: Trees, keywords: ['tree', 'forest', 'nature', 'green', 'woodland', 'environment'] },
    { name: 'tree-pine', component: TreePine, keywords: ['pine', 'evergreen', 'forest', 'nature', 'conifer', 'tree'] },
    { name: 'mountain', component: Mountain, keywords: ['mountain', 'snow', 'peak', 'cold', 'high', 'climb'] },
    { name: 'tent', component: Tent, keywords: ['camping', 'tent', 'outdoor', 'nature', 'adventure', 'site'] },
    { name: 'globe', component: Globe, keywords: ['earth', 'world', 'globe', 'planet', 'environment', 'global'] },
    { name: 'thermometer', component: Thermometer, keywords: ['temperature', 'hot', 'cold', 'measure', 'climate', 'weather'] },
    { name: 'wind', component: Wind, keywords: ['wind', 'breeze', 'air', 'weather', 'blow', 'atmosphere'] },
  ],
  'Science & Lab': [
    { name: 'microscope', component: Microscope, keywords: ['microscope', 'science', 'lab', 'research', 'study', 'magnify'] },
    { name: 'test-tube', component: TestTube, keywords: ['test', 'tube', 'science', 'lab', 'chemistry', 'experiment'] },
    { name: 'test-tubes', component: TestTubes, keywords: ['test', 'tubes', 'science', 'lab', 'chemistry', 'experiment'] },
    { name: 'flask-conical', component: FlaskConical, keywords: ['flask', 'beaker', 'chemistry', 'lab', 'science', 'experiment'] },
    { name: 'telescope', component: Telescope, keywords: ['telescope', 'astronomy', 'space', 'star', 'observe', 'lens'] },
    { name: 'ruler', component: Ruler, keywords: ['ruler', 'measure', 'length', 'straight', 'tool', 'scale'] },
    { name: 'scale', component: Scale, keywords: ['scale', 'balance', 'weight', 'justice', 'measure', 'equal'] },
    { name: 'thermometer', component: Thermometer, keywords: ['thermometer', 'temperature', 'hot', 'cold', 'measure', 'fever'] },
    { name: 'pill', component: Pill, keywords: ['pill', 'medicine', 'drug', 'tablet', 'health', 'pharmacy'] },
    { name: 'dna', component: Dna, keywords: ['dna', 'genetics', 'biology', 'helix', 'chromosome', 'gene'] },
    { name: 'atom', component: Atom, keywords: ['atom', 'atomic', 'nuclear', 'physics', 'science', 'molecule'] },
    { name: 'battery', component: Battery, keywords: ['battery', 'power', 'energy', 'charge', 'electric', 'cell'] },
    { name: 'zap', component: Zap, keywords: ['lightning', 'electric', 'power', 'energy', 'bolt', 'shock'] },
    { name: 'magnet', component: Magnet, keywords: ['magnet', 'magnetic', 'attract', 'force', 'pull', 'field'] },
  ],
  'Tools & Equipment': [
    { name: 'wrench', component: Wrench, keywords: ['wrench', 'tool', 'fix', 'repair', 'maintenance', 'spanner'] },
    { name: 'hammer', component: Hammer, keywords: ['hammer', 'tool', 'nail', 'build', 'construction', 'hit'] },
    { name: 'cog', component: Cog, keywords: ['gear', 'cog', 'settings', 'mechanical', 'wheel', 'config'] },
    { name: 'pickaxe', component: Pickaxe, keywords: ['pick', 'mining', 'dig', 'excavate', 'tool', 'pickaxe'] },
    { name: 'axe', component: Axe, keywords: ['axe', 'wood', 'chop', 'cut', 'lumber', 'tool'] },
    { name: 'nut', component: Nut, keywords: ['bolt', 'nut', 'screw', 'fastener', 'hardware', 'thread'] },
    { name: 'plug', component: Plug, keywords: ['plug', 'electric', 'power', 'outlet', 'connection', 'socket'] },
    { name: 'lightbulb', component: Lightbulb, keywords: ['bulb', 'light', 'idea', 'bright', 'lamp', 'illumination'] },
    { name: 'flashlight', component: Flashlight, keywords: ['flashlight', 'torch', 'light', 'beam', 'battery', 'portable'] },
  ],
  'Location & Navigation': [
    { name: 'map-pin', component: MapPin, keywords: ['pin', 'location', 'map', 'marker', 'place', 'position'] },
    { name: 'compass', component: Compass, keywords: ['compass', 'direction', 'navigation', 'north', 'guide', 'orient'] },
    { name: 'home', component: Home, keywords: ['house', 'home', 'building', 'residence', 'property', 'dwelling'] },
    { name: 'building-2', component: Building2, keywords: ['office', 'building', 'corporate', 'business', 'work', 'skyscraper'] },
    { name: 'factory', component: Factory, keywords: ['factory', 'industry', 'manufacturing', 'plant', 'production', 'building'] },
    { name: 'construction', component: Construction, keywords: ['construction', 'building', 'crane', 'site', 'develop', 'work'] },
    { name: 'target', component: Target, keywords: ['target', 'bullseye', 'aim', 'goal', 'objective', 'focus'] },
    { name: 'satellite', component: Satellite, keywords: ['satellite', 'dish', 'antenna', 'signal', 'communication', 'receive'] },
    { name: 'globe', component: Globe, keywords: ['globe', 'world', 'earth', 'map', 'international', 'geography'] },
  ],
  'Communication': [
    { name: 'phone', component: Phone, keywords: ['phone', 'telephone', 'call', 'receiver', 'communication', 'talk'] },
    { name: 'smartphone', component: Smartphone, keywords: ['mobile', 'phone', 'smartphone', 'cell', 'device', 'call'] },
    { name: 'mail', component: Mail, keywords: ['email', 'mail', 'message', 'send', 'communication', 'letter'] },
    { name: 'at-sign', component: AtSign, keywords: ['email', 'at', 'address', 'contact', 'internet', 'handle'] },
    { name: 'inbox', component: Inbox, keywords: ['inbox', 'receive', 'mail', 'message', 'notification', 'letter'] },
    { name: 'send', component: Send, keywords: ['send', 'outbox', 'mail', 'message', 'share', 'submit'] },
    { name: 'megaphone', component: Megaphone, keywords: ['megaphone', 'announcement', 'loud', 'speaker', 'broadcast', 'public'] },
    { name: 'bell', component: Bell, keywords: ['bell', 'notification', 'alert', 'ring', 'sound', 'reminder'] },
  ],
  'Time & Calendar': [
    { name: 'alarm-clock', component: AlarmClock, keywords: ['alarm', 'clock', 'time', 'wake', 'alert', 'morning'] },
    { name: 'timer', component: Timer, keywords: ['timer', 'clock', 'stopwatch', 'countdown', 'time', 'measure'] },
    { name: 'hourglass', component: Hourglass, keywords: ['hourglass', 'flowing', 'sand', 'time', 'wait', 'loading'] },
    { name: 'clock', component: Clock, keywords: ['clock', 'time', 'hour', 'oclock', 'schedule', 'watch'] },
    { name: 'calendar', component: Calendar, keywords: ['calendar', 'date', 'schedule', 'day', 'month', 'year'] },
    { name: 'watch', component: Watch, keywords: ['watch', 'smartwatch', 'time', 'wrist', 'device', 'wearable'] },
  ],
  'Status & Alerts': [
    { name: 'circle-check', component: CircleCheck, keywords: ['check', 'mark', 'correct', 'done', 'yes', 'approved'] },
    { name: 'circle-x', component: CircleX, keywords: ['cross', 'mark', 'wrong', 'no', 'error', 'delete'] },
    { name: 'alert-triangle', component: AlertTriangle, keywords: ['warning', 'caution', 'alert', 'danger', 'attention', 'yellow'] },
    { name: 'circle-alert', component: CircleAlert, keywords: ['exclamation', 'mark', 'important', 'alert', 'attention', 'red'] },
    { name: 'help-circle', component: HelpCircle, keywords: ['question', 'mark', 'ask', 'help', 'unknown', 'confused'] },
    { name: 'bell', component: Bell, keywords: ['bell', 'notification', 'alert', 'ring', 'sound', 'reminder'] },
    { name: 'siren', component: Siren, keywords: ['police', 'car', 'light', 'siren', 'emergency', 'alert'] },
    { name: 'lightbulb', component: Lightbulb, keywords: ['bulb', 'idea', 'light', 'bright', 'think', 'solution'] },
    { name: 'flame', component: Flame, keywords: ['fire', 'flame', 'hot', 'burn', 'heat', 'popular'] },
    { name: 'star', component: Star, keywords: ['star', 'favorite', 'rating', 'quality', 'best', 'yellow'] },
    { name: 'trophy', component: Trophy, keywords: ['trophy', 'award', 'winner', 'champion', 'first', 'prize'] },
    { name: 'target', component: Target, keywords: ['target', 'bullseye', 'goal', 'aim', 'objective', 'focus'] },
  ],
  'People & Users': [
    { name: 'user', component: User, keywords: ['person', 'user', 'profile', 'silhouette', 'individual', 'account'] },
    { name: 'users', component: Users, keywords: ['people', 'users', 'group', 'team', 'multiple', 'crowd'] },
    { name: 'user-check', component: UserCheck, keywords: ['user', 'approved', 'verified', 'confirmed', 'check', 'active'] },
    { name: 'user-plus', component: UserPlus, keywords: ['user', 'add', 'new', 'create', 'invite', 'register'] },
    { name: 'user-x', component: UserX, keywords: ['user', 'remove', 'delete', 'ban', 'deactivate', 'block'] },
  ],
  'Safety & Protection': [
    { name: 'hard-hat', component: HardHat, keywords: ['safety', 'helmet', 'hat', 'protection', 'construction', 'hard'] },
    { name: 'glasses', component: Glasses, keywords: ['goggles', 'safety', 'glasses', 'protection', 'eyes', 'lab'] },
    { name: 'shield', component: Shield, keywords: ['shield', 'protection', 'defense', 'security', 'guard', 'safety'] },
    { name: 'shield-alert', component: ShieldAlert, keywords: ['biohazard', 'danger', 'toxic', 'hazardous', 'warning', 'contamination'] },
    { name: 'alert-triangle', component: AlertTriangle, keywords: ['warning', 'caution', 'alert', 'danger', 'attention', 'yellow'] },
    { name: 'ban', component: Ban, keywords: ['prohibited', 'no', 'ban', 'stop', 'forbidden', 'not'] },
    { name: 'lock', component: Lock, keywords: ['lock', 'secure', 'safety', 'protected', 'private', 'closed'] },
    { name: 'siren', component: Siren, keywords: ['police', 'car', 'light', 'siren', 'emergency', 'alert'] },
    { name: 'bandage', component: Bandage, keywords: ['bandage', 'adhesive', 'first', 'aid', 'medical', 'injury'] },
  ],
  'Industrial & Construction': [
    { name: 'construction', component: Construction, keywords: ['construction', 'crane', 'building', 'site', 'develop', 'work'] },
    { name: 'factory', component: Factory, keywords: ['factory', 'industry', 'manufacturing', 'plant', 'production', 'smoke'] },
    { name: 'cog', component: Cog, keywords: ['gear', 'machinery', 'mechanical', 'industrial', 'equipment', 'parts'] },
    { name: 'nut', component: Nut, keywords: ['bolt', 'nut', 'fastener', 'hardware', 'construction', 'assembly'] },
    { name: 'wrench', component: Wrench, keywords: ['wrench', 'tool', 'maintenance', 'repair', 'mechanical', 'fix'] },
    { name: 'hammer', component: Hammer, keywords: ['hammer', 'construction', 'tool', 'build', 'demolition', 'break'] },
    { name: 'pickaxe', component: Pickaxe, keywords: ['pickaxe', 'mining', 'excavation', 'demolition', 'breaking', 'dig'] },
    { name: 'axe', component: Axe, keywords: ['axe', 'demolition', 'cutting', 'breaking', 'wood', 'removal'] },
    { name: 'brick-wall', component: BrickWall, keywords: ['brick', 'building', 'construction', 'material', 'wall', 'structure'] },
    { name: 'ruler', component: Ruler, keywords: ['ruler', 'measurement', 'length', 'scale', 'planning', 'design'] },
    { name: 'zap', component: Zap, keywords: ['electrical', 'power', 'energy', 'hazard', 'voltage', 'shock'] },
    { name: 'plug', component: Plug, keywords: ['plug', 'electrical', 'power', 'connection', 'outlet', 'wire'] },
    { name: 'lightbulb', component: Lightbulb, keywords: ['light', 'bulb', 'electrical', 'illumination', 'fixture', 'lamp'] },
  ],
  'Medical & Health': [
    { name: 'hospital', component: Hospital, keywords: ['hospital', 'medical', 'health', 'clinic', 'healthcare', 'facility'] },
    { name: 'stethoscope', component: Stethoscope, keywords: ['stethoscope', 'doctor', 'medical', 'examination', 'health', 'checkup'] },
    { name: 'syringe', component: Syringe, keywords: ['syringe', 'injection', 'vaccine', 'medical', 'needle', 'shot'] },
    { name: 'pill', component: Pill, keywords: ['pill', 'medicine', 'medication', 'drug', 'tablet', 'treatment'] },
    { name: 'dna', component: Dna, keywords: ['dna', 'genetics', 'medical', 'biology', 'health', 'testing'] },
    { name: 'test-tube', component: TestTube, keywords: ['test', 'tube', 'medical', 'laboratory', 'sample', 'analysis'] },
    { name: 'microscope', component: Microscope, keywords: ['microscope', 'medical', 'laboratory', 'analysis', 'research', 'study'] },
    { name: 'thermometer', component: Thermometer, keywords: ['thermometer', 'temperature', 'fever', 'medical', 'health', 'measurement'] },
    { name: 'bandage', component: Bandage, keywords: ['bandage', 'first', 'aid', 'injury', 'wound', 'treatment'] },
    { name: 'heart', component: Heart, keywords: ['heart', 'health', 'medical', 'cardiology', 'vital', 'organ'] },
    { name: 'brain', component: Brain, keywords: ['brain', 'neurology', 'mental', 'health', 'cognition', 'organ'] },
    { name: 'eye', component: Eye, keywords: ['eye', 'vision', 'sight', 'ophthalmology', 'examination', 'health'] },
  ],
  'Quality & Compliance': [
    { name: 'circle-check', component: CircleCheck, keywords: ['approved', 'compliant', 'passed', 'verified', 'certified', 'check'] },
    { name: 'circle-x', component: CircleX, keywords: ['failed', 'non-compliant', 'rejected', 'error', 'violation', 'cross'] },
    { name: 'clipboard-check', component: ClipboardCheck, keywords: ['audit', 'checklist', 'compliance', 'inspection', 'assessment', 'review'] },
    { name: 'bar-chart-3', component: BarChart3, keywords: ['metrics', 'quality', 'performance', 'statistics', 'compliance', 'data'] },
    { name: 'target', component: Target, keywords: ['target', 'goal', 'objective', 'standard', 'compliance', 'aim'] },
    { name: 'scale', component: Scale, keywords: ['balance', 'compliance', 'regulation', 'law', 'standard', 'justice'] },
    { name: 'search', component: Search, keywords: ['inspection', 'audit', 'examination', 'review', 'investigation', 'search'] },
    { name: 'award', component: Award, keywords: ['award', 'excellence', 'quality', 'achievement', 'certification', 'prize'] },
    { name: 'trophy', component: Trophy, keywords: ['trophy', 'award', 'winner', 'champion', 'first', 'prize'] },
    { name: 'shield', component: Shield, keywords: ['protection', 'compliance', 'security', 'safety', 'shield', 'defense'] },
    { name: 'bookmark', component: Bookmark, keywords: ['bookmark', 'standard', 'reference', 'guideline', 'procedure', 'mark'] },
  ],
};

// ─── Category Icons ──────────────────────────────────────────────────

export const CATEGORY_ICONS = {
  'General': Shapes,
  'Business & Finance': Briefcase,
  'Technology': Laptop,
  'Transportation': Car,
  'Nature & Environment': Leaf,
  'Forms & Documents': ClipboardList,
  'Water & Environment': Droplets,
  'Science & Lab': FlaskConical,
  'Tools & Equipment': Wrench,
  'Location & Navigation': MapPin,
  'Communication': Phone,
  'Time & Calendar': Calendar,
  'Status & Alerts': AlertTriangle,
  'People & Users': Users,
  'Safety & Protection': Shield,
  'Industrial & Construction': Construction,
  'Medical & Health': Hospital,
  'Quality & Compliance': ClipboardCheck,
};
